/**
 * Hero — eyebrow, headline, subhead, CTAs (sign up + jump to the demos), proof
 * chips and a REAL production screenshot (the "Today" page) in a browser frame.
 * The image has intrinsic dimensions and is eager/high-priority (LCP) so the
 * hero never shifts layout.
 */

import { Icon, PrimaryCta, useLandingCta } from './shared';
import { BrowserFrame, MediaPicture } from './MediaPicture';
import { useT } from '@/i18n';

export function Hero() {
  const { t } = useT();
  const { signup } = useLandingCta();

  return (
    <section id="top" className="relative overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/[0.07] via-transparent to-transparent" />
        <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute -right-16 top-24 h-80 w-80 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      </div>

      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-4 pb-16 pt-14 sm:px-6 sm:pb-20 sm:pt-20 lg:grid-cols-12 lg:gap-12 lg:px-8 lg:pb-28 lg:pt-24">
        <div className="um-animate-page lg:col-span-5">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary shadow-elevation-1">
            <Icon name="sparkles" className="h-4 w-4" strokeWidth={2} />
            {t('routes.landing.v2.hero.eyebrow')}
          </span>
          <h1 className="mt-5 text-balance text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
            {t('routes.landing.v2.hero.title')}
          </h1>
          <p className="mt-6 max-w-xl text-pretty text-lg leading-relaxed text-muted">{t('routes.landing.v2.hero.subtitle')}</p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <PrimaryCta onClick={() => signup()}>
              {t('routes.landing.v2.hero.ctaPrimary')}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="h-5 w-5">
                <path d="M4.5 12h15m0 0-6.75-6.75M19.5 12l-6.75 6.75" />
              </svg>
            </PrimaryCta>
            <a
              href="#demo"
              data-testid="hero-demo-link"
              className="inline-flex items-center justify-center gap-2 rounded-token border border-border bg-surface px-6 py-3 text-base font-semibold text-foreground shadow-elevation-1 transition hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg motion-reduce:transition-none"
            >
              <Icon name="play" className="h-5 w-5 fill-current" strokeWidth={0} />
              {t('routes.landing.v2.hero.ctaSecondary')}
            </a>
          </div>

          <ul className="mt-8 flex flex-wrap gap-x-5 gap-y-2">
            {(['chip1', 'chip2', 'chip3', 'chip4'] as const).map((chip) => (
              <li key={chip} className="inline-flex items-center gap-1.5 text-sm text-muted">
                <Icon name="check" className="h-4 w-4 text-emerald-500" strokeWidth={2.2} />
                {t(`routes.landing.v2.hero.${chip}`)}
              </li>
            ))}
          </ul>

          <p className="mt-6 inline-flex items-start gap-2 text-sm text-muted">
            <Icon name="shield" className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" strokeWidth={1.9} />
            <span>{t('routes.landing.v2.hero.note')}</span>
          </p>
        </div>

        <div className="relative lg:col-span-7">
          <div className="relative mx-auto w-full max-w-3xl">
            <div aria-hidden="true" className="absolute -inset-4 -z-10 rounded-token-xl bg-gradient-to-tr from-indigo-500/25 to-violet-500/25 blur-2xl" />
            <BrowserFrame>
              <MediaPicture id="today" eager alt={t('routes.landing.v2.hero.imgAlt')} />
            </BrowserFrame>
            <div className="absolute -bottom-5 -left-3 hidden items-center gap-2 rounded-token-lg border border-border bg-surface px-3 py-2 shadow-elevation-3 sm:flex">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-token bg-gradient-to-br from-emerald-500 to-teal-500 text-white">
                <Icon name="robot" className="h-5 w-5" strokeWidth={1.9} />
              </span>
              <div className="text-xs">
                <p className="font-semibold text-foreground">{t('routes.landing.v2.hero.badgeTitle')}</p>
                <p className="text-muted">{t('routes.landing.v2.hero.badgeText')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
